import React, { PropTypes, Component } from 'react'

// SoundCloud shut down its legacy `api.soundcloud.com/resolve.json` endpoint,
// so the old client_id based `soundcloud-audio` resolver now returns 401 for
// every key. The HTML5 Widget API (w.soundcloud.com/player) still works, needs
// no client_id, and can play these private secret-token tracks. This container
// loads that widget into a hidden iframe and exposes a small controller with
// the same play/pause/resolve shape the rest of the app already expects.

const WIDGET_API_URL = 'https://w.soundcloud.com/player/api.js'

let apiPromise = null
function loadWidgetApi () {
  if (window.SC && window.SC.Widget) return Promise.resolve(window.SC)
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = WIDGET_API_URL
      script.async = true
      script.onload = () => resolve(window.SC)
      script.onerror = reject
      document.body.appendChild(script)
    })
  }
  return apiPromise
}

// A private track's share URL carries its secret token as a path segment
// (…/track/s-TOKEN). That form works for the human-facing page but the widget
// resolver 404s on it — it wants the token as a `secret_token` query param
// (…/track?secret_token=s-TOKEN). Rewrite it here; leave anything else (public
// URLs, or a URL already in query form) untouched.
function embedTrack (track) {
  const match = /^(https?:\/\/soundcloud\.com\/[^?#]+)\/(s-[^/?#]+)\/?$/.exec(track)
  return match ? match[1] + '?secret_token=' + match[2] : track
}

function widgetUrl (track) {
  const params = [
    'url=' + encodeURIComponent(embedTrack(track)),
    'auto_play=false',
    'visual=false',
    'show_artwork=false',
    'sharing=false',
    'buying=false',
    'download=false',
    'show_comments=false',
    'show_playcount=false',
    'show_user=false',
    'hide_related=true'
  ]
  return 'https://w.soundcloud.com/player/?' + params.join('&')
}

class SoundCloudPlayer extends Component {
  constructor (props) {
    super(props)
    this.state = {playing: false}
    // The iframe only ever loads the initial track; every subsequent track is
    // swapped in via widget.load() so we never tear down the widget binding.
    this.initialUrl = props.resolveUrl
    this.widget = null
    this.ready = false
    this.pendingPlay = false

    this.controller = {
      play: () => {
        if (this.widget && this.ready) {
          this.widget.play()
        } else {
          this.pendingPlay = true
        }
      },
      pause: () => {
        this.pendingPlay = false
        if (this.widget && this.ready) this.widget.pause()
      },
      // Mirrors soundcloud-audio's resolve(url, callback): load a new track and
      // start playing it. Kept named `resolve` so App.playSong is unchanged.
      resolve: (url, callback) => {
        if (!this.widget) return
        this.widget.load(embedTrack(url), {
          auto_play: true,
          callback: () => { if (callback) callback() }
        })
      }
    }
  }
  componentDidMount () {
    loadWidgetApi().then((SC) => {
      if (!this.iframe) return
      this.widget = SC.Widget(this.iframe)
      this.widget.bind(SC.Widget.Events.READY, () => {
        this.ready = true
        if (this.pendingPlay) {
          this.pendingPlay = false
          this.widget.play()
        }
      })
      this.widget.bind(SC.Widget.Events.PLAY, () => this.setState({playing: true}))
      this.widget.bind(SC.Widget.Events.PAUSE, () => this.setState({playing: false}))
      this.widget.bind(SC.Widget.Events.FINISH, () => this.setState({playing: false}))
    }).catch(() => {})
  }
  render () {
    return (
      <div>
        <iframe
          ref={(el) => { this.iframe = el }}
          title='SoundCloud player'
          src={widgetUrl(this.initialUrl)}
          allow='autoplay'
          scrolling='no'
          frameBorder='no'
          style={{position: 'fixed', left: '-9999px', top: '-9999px', width: '1px', height: '1px', border: 0}}
        />
        {React.Children.map(this.props.children, (child) =>
          React.cloneElement(child, {
            playing: this.state.playing,
            soundCloudAudio: this.controller
          })
        )}
      </div>
    )
  }
}

SoundCloudPlayer.propTypes = {
  resolveUrl: PropTypes.string,
  children: PropTypes.node
}

export default SoundCloudPlayer



/** WEBPACK FOOTER **
 ** ./src/components/SoundCloudPlayer.js
 **/